<template>
    <AddFileSection
        :id="AddFileFormSectionIds.REQUEST_MORTGAGE_INFORMATION"
        sectionDescription="Select clients to request their mortgage details and a signed authorization letter."
        sectionHeading="Request seller mortgage information"
    >
        <template #trailing>
            <DsBadge
                text="New"
                variant="active"
            />
        </template>

        <ClientMultiSelect
            v-model="selectedClients"
            class="my-3"
            :clientOptions
            :required="false"
        />

        <PayoffRetrievalEnabledSection
            v-if="featureFlagsStore.flags?.payoff_retrieval"
            v-model:autoRetrieve="autoRetrieve"
            v-model:localCloseDate="localCloseDate"
            v-model:localGoodThroughDate="localGoodThroughDate"
            sideBySideDates
        />
    </AddFileSection>
</template>

<script setup lang="ts">
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { availableClients } from '@/company-portal/add-file/available-clients';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientMultiSelect from '@/components/Views/Company/AddFile/ClientMultiSelect.vue';
import type { ClientOption, ClientType } from '@/components/Views/Company/File/file.types';
import PayoffRetrievalEnabledSection from '@/components/Views/Company/File/SellerAuth/PayoffRetrievalEnabledSection.vue';
import { isBuyerOrSeller } from '@/components/Views/Company/File/SellerAuth/seller-auth-utils';
import { joinFirstLast } from '@/shared/utils/strings';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import DsBadge from '@ds/badge/DsBadge.vue';
import type { DateValue } from 'reka-ui';
import { computed, ref } from 'vue';

const featureFlagsStore = useFeatureFlagsStore();

const autoRetrieve = ref(true);
const localCloseDate = ref<DateValue>();
const localGoodThroughDate = ref<DateValue>();
const selectedClients = ref<ClientOption[]>([]);

const clientOptions = computed<ClientOption[]>(() =>
    availableClients.new.map(client => ({
        id: client.id,
        value: client.id,
        email: client.email,
        first: client.first,
        last: client.last,
        label: joinFirstLast(client.first, client.last),
        phone: client.phone,
        /** @todo cleanup in [CLENG-883] assertion is needed because we have duplicate type declarations for client role */
        role: client.role as ClientType,
        disabled: !isBuyerOrSeller(client.role as ClientType),
        helperText: !isBuyerOrSeller(client.role as ClientType) ? 'This task requires a buyer or seller role.' : undefined,
    })),
);
</script>
